import * as React from "react";
import { observer, inject } from "mobx-react";
import BootstrapTable from "react-bootstrap-table-next";
import { UncontrolledTooltip } from "reactstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faBan, faTrashAlt, faPhone } from "@fortawesome/free-solid-svg-icons";

import { Contact, Business, SimpleCustomer, Customer, BusinessContactRelationship, ExtractFriendlyName } from "src/model/Customer";

import { CustomerEndpoints } from "../../endpoints/CustomerEndpoints";
import { CustomerStore } from "../../stores/CustomerStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import CustomerSelect from "../common/CustomerSelect";
import { SimpleConfirmation, SimpleAlert } from "../common/SimpleConfirmAlert";

import { successMessage, errorMessage } from "../common/Toast";

const NONE_ID: number = -1;

export interface Props {
    customerStore?: CustomerStore;
    configurationStore?: ConfigurationStore;
    customerEndpoints?: CustomerEndpoints;
}

export interface State {
    wasValidated: boolean;
    selectedRelationshipCustomer: SimpleCustomer | null;
    relationshipCustomerTitle: string;
}

const EmptyState = (): State => {
    return {
        wasValidated: false,
        selectedRelationshipCustomer: null,
        relationshipCustomerTitle: ""
    };
};

@inject("customerStore")
@inject("configurationStore")
@inject("customerEndpoints")
@observer
export default class BusinessContactRelationshipPanel extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = EmptyState();
    }

    submitContacts = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (this.state.relationshipCustomerTitle !== "" && this.state.selectedRelationshipCustomer) {
            this.setState({ wasValidated: false });
            this.props
                .customerStore!.addContactToBusiness(this.state.selectedRelationshipCustomer.id, this.state.relationshipCustomerTitle)
                .then(() => {
                    successMessage(`${ExtractFriendlyName(this.state.selectedRelationshipCustomer!)} 
                        has been linked with a relationship of '${this.state.relationshipCustomerTitle}'.`);
                    this.setState(EmptyState());
                })
                .catch(error => errorMessage(error));
        } else {
            this.setState({ wasValidated: true });
        }
    };

    delete = (customer: SimpleCustomer) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        event.stopPropagation();

        const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete the relationship with ${ExtractFriendlyName(customer)}?`);
        if (!goAhead) {
            return;
        }

        this.props
            .customerStore!.removeContactFromBusiness(customer.id)
            .then(() => successMessage(`This relationship has been deleted.`))
            .catch(error => errorMessage(error));
    };

    render() {
        const customerStore = this.props.customerStore!;
        const configurationStore = this.props.configurationStore!;

        const customer = customerStore.customer as Business;
        if (!customer) {
            return <div />;
        }

        const columns = [
            {
                dataField: "contact.firstName",
                text: "First name"
            },
            {
                dataField: "contact.lastName",
                text: "Last name"
            },
            {
                dataField: "title",
                text: "Title"
            },
            {
                dataField: "id",
                text: "",
                headerStyle: { width: "100px" },
                formatter: (cellContent: any, row: BusinessContactRelationship) => {
                    return (
                        <div className="">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                id={`tooltipDelete${row.contact.id}`}
                                title="Delete Relationship"
                                onClick={this.delete(row.contact)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDelete${row.contact.id}`} delay={0} className="tooltipstrap">
                                Delete Relationship
                            </UncontrolledTooltip>
                        </div>
                    );
                }
            }
        ];

        return (
            <div className="card shadow mt-3">
                <div className="card-header">Linked Contacts</div>
                <div className="card-body">
                    <div className="col-md-12 col-lg-11 col-lx-9 mx-auto">
                        <BootstrapTable
                            keyField={"Contacts"}
                            bordered={false}
                            bootstrap4={true}
                            striped={true}
                            columns={columns}
                            data={(customer as Business).contacts}
                            noDataIndication={() => "No contacts linked to this business at the moment"}
                        />

                        <form
                            id="link-contacts"
                            className={"needs-validation" + (this.state.wasValidated ? " was-validated" : "")}
                            onSubmit={e => this.submitContacts(e)}
                            noValidate={true}
                        >
                            <div className="form-row">
                                <div className="form-group col-sm-12 col-md-5">
                                    <label htmlFor="email">
                                        Contact <span className="text-danger">*</span>{" "}
                                    </label>
                                    <CustomerSelect
                                        customerEndpoints={this.props.customerEndpoints!}
                                        include="CONTACT"
                                        showCreate={true}
                                        value={this.state.selectedRelationshipCustomer}
                                        onCreate={selectedRelationshipCustomer => this.setState({ selectedRelationshipCustomer })}
                                        onChange={selectedRelationshipCustomer => this.setState({ selectedRelationshipCustomer })}
                                    />
                                </div>
                                <div className="form-group col-sm-12 col-md-4">
                                    <label htmlFor="contactTitle">
                                        Title <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="relationshipCustomerTitle"
                                        className="form-control"
                                        placeholder="Contact Title"
                                        value={this.state.relationshipCustomerTitle}
                                        onChange={e => {
                                            e.preventDefault();
                                            this.setState({ relationshipCustomerTitle: e.target.value });
                                        }}
                                        required={true}
                                    />
                                </div>
                                <div className="form-group col-sm-12 col-md-3 pt-4">
                                    <button type="submit" className="btn btn-primary mt-2 btn-block">
                                        {" "}
                                        Add Relationship{" "}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/BusinessContactRelationshipPanel.tsx